import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, X, ShieldCheck, Sparkles, Sun, Moon, Minus,
  Target, Users, Send, Inbox, GitBranch, Gauge,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { PublicFooter } from "@/components/PublicFooter";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { startCheckout } from "@/lib/checkout";
import { Button } from "@/components/ui/button";
import { DemoRequestDialog } from "@/components/DemoRequestDialog";

const CAPABILITIES = [
  { icon: Target, title: "Offer → ICP intelligence", body: "Describe any B2B offer. The Copilot returns a sharp ICP brief, three messaging angles, and a recommended multi-touch sequence." },
  { icon: Users, title: "Licensed data only", body: "Build lists from official and licensed sources. Every public-sector record stores a source URL. No LinkedIn scraping. Ever." },
  { icon: Send, title: "Approval-gated outbound", body: "AI drafts personalized email and LinkedIn messages. First-touch approval is on by default so nothing off-brand ships." },
  { icon: Inbox, title: "Reply intelligence", body: "Every reply is classified - interested, objection, OOO, referral, meeting - with a next-best action and a booking path." },
  { icon: GitBranch, title: "Native pipeline", body: "Move deals from Lead to Won across eight stages without leaving the control plane. No CRM tax." },
  { icon: Gauge, title: "Cost per meeting, live", body: "Meter every send against booked meetings. See cost per meeting and quota attainment on one executive dashboard." },
];

const COMPARISON = [
  ["Licensed & official data sources", true, false, "partial"],
  ["AI Outbound Copilot (versioned prompts)", true, false, true],
  ["First-touch approval queue", true, false, false],
  ["Native CRM pipeline", true, false, "partial"],
  ["Public-sector procurement module", true, false, false],
  ["Compliance built-in (CAN-SPAM, suppression)", true, "partial", "partial"],
  ["Cost-per-meeting analytics", true, false, false],
  ["Positioned for senior revenue teams", true, false, false],
];

export default function Landing() {
  const { theme, toggle } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);
  const [plans, setPlans] = useState([]);
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  const onSelectPlan = async (plan) => {
    if (plan.id === "enterprise") { window.location.href = "mailto:sales@dealsops.example?subject=DealsOps Enterprise"; return; }
    const lookup = annual ? plan.lookup_yearly : plan.lookup_monthly;
    if (user) { await startCheckout(lookup); }
    else { navigate(`/register?plan=${lookup}`); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-x-hidden">
      <DemoRequestDialog open={demoOpen} onOpenChange={setDemoOpen} />
      {/* Nav */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#capabilities" className="hover:text-foreground transition-colors">Platform</a>
            <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
            <a href="#compare" className="hover:text-foreground transition-colors">Compare</a>
          </nav>
          <div className="flex items-center gap-2">
            <button onClick={toggle} data-testid="theme-toggle-button" className="h-9 w-9 rounded-md border border-border/70 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            {user ? (
              <Button onClick={() => navigate("/app")} data-testid="go-to-app-btn" className="rounded-md">Open app</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn" className="rounded-md hidden sm:inline-flex">Sign in</Button>
                <Button onClick={() => setDemoOpen(true)} data-testid="nav-demo-btn" className="rounded-md bg-amber-500 text-black hover:bg-amber-400">Request a demo</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 grain opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-16 sm:pt-28 sm:pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="eyebrow mb-5">The B2B Sales Control Plane</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                From any offer to a booked meeting -<span className="text-amber-500"> one operating system.</span>
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-xl">
                DealsOps finds the right buyers from licensed sources, writes and approves outbound, classifies every reply, books meetings, and moves deals to close. Built for senior revenue teams - not mass mailers.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button onClick={() => setDemoOpen(true)} data-testid="hero-cta-btn" className="rounded-md h-11 px-6 bg-amber-500 text-black hover:bg-amber-400 text-base">
                  Request a demo <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <a href="#pricing"><Button variant="outline" className="rounded-md h-11 px-6 text-base">See pricing</Button></a>
              </div>
              <div className="mt-8 flex items-center gap-5 text-xs text-muted-foreground font-mono">
                <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-500" /> CAN-SPAM built in</span>
                <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-500" /> Licensed data only</span>
                <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-500" /> No free tier</span>
              </div>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="lg:col-span-5">
            <div className="rounded-xl border border-border/70 bg-card/60 amber-glow overflow-hidden">
              <div className="border-b border-border/60 px-5 py-3 flex items-center justify-between">
                <span className="text-xs font-mono text-muted-foreground">command-center</span>
                <span className="text-xs font-mono text-amber-500">live</span>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-border/60 p-4">
                    <div className="text-xs text-muted-foreground">Cost / meeting</div>
                    <div className="metric text-2xl mt-1 text-amber-500">$41</div>
                  </div>
                  <div className="rounded-lg border border-border/60 p-4">
                    <div className="text-xs text-muted-foreground">Reply rate</div>
                    <div className="metric text-2xl mt-1 text-emerald-500">12.4%</div>
                  </div>
                </div>
                <div className="rounded-lg border border-border/60 p-4 space-y-2.5">
                  {[["Leads", 92], ["Sequenced", 71], ["Replied", 34], ["Meetings", 12], ["Won", 4]].map(([k, v]) => (
                    <div key={k} className="flex items-center gap-3">
                      <span className="w-20 text-xs text-muted-foreground">{k}</span>
                      <div className="flex-1 h-2 rounded-full bg-accent overflow-hidden">
                        <div className="h-full bg-amber-500/80 rounded-full" style={{ width: `${v}%` }} />
                      </div>
                      <span className="w-8 text-right text-xs font-mono">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section id="capabilities" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 border-t border-border/50">
        <div className="max-w-2xl">
          <div className="eyebrow mb-4">One plane, four disciplines</div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Everything a revenue team needs to go from offer to close.</h2>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border/60 rounded-xl overflow-hidden border border-border/60">
          {CAPABILITIES.map((c, i) => (
            <motion.div key={c.title} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="bg-card p-8 group">
              <c.icon className="h-6 w-6 text-amber-500 mb-5" strokeWidth={1.75} />
              <h3 className="font-display text-lg font-semibold tracking-tight">{c.title}</h3>
              <p className="mt-2.5 text-sm text-muted-foreground leading-relaxed">{c.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 border-t border-border/50">
        <div className="flex flex-col items-center text-center">
          <div className="eyebrow mb-4">Pricing</div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl">A paid control plane. No free plan, no $49 tier.</h2>
          <p className="mt-3 text-sm text-muted-foreground" data-testid="pricing-no-trial">No free plan. No trial. Demo is scheduled.</p>
          <div className="mt-8 inline-flex items-center gap-1 rounded-full border border-border/70 p-1 bg-card/60">
            <button onClick={() => setAnnual(false)} data-testid="billing-monthly" className={`px-4 py-1.5 rounded-full text-sm transition-colors ${!annual ? "bg-amber-500 text-black" : "text-muted-foreground"}`}>Monthly</button>
            <button onClick={() => setAnnual(true)} data-testid="billing-annual" className={`px-4 py-1.5 rounded-full text-sm transition-colors ${annual ? "bg-amber-500 text-black" : "text-muted-foreground"}`}>Annual <span className="text-xs opacity-80">· 2 months free</span></button>
          </div>
        </div>

        <div className="mt-14 grid lg:grid-cols-4 gap-5">
          {plans.map((p) => {
            const featured = p.id === "professional";
            const price = p.id === "enterprise" ? "Custom" : `$${annual ? Math.round(p.yearly / 12) : p.monthly}`;
            return (
              <div key={p.id} data-testid={`pricing-tier-${p.id}`}
                className={`rounded-xl border p-6 flex flex-col relative ${featured ? "border-amber-500/60 bg-card amber-glow" : "border-border/70 bg-card/50"}`}>
                {featured && <span className="absolute -top-2.5 left-6 text-[10px] font-mono uppercase tracking-wider bg-amber-500 text-black px-2 py-0.5 rounded-full">Most popular</span>}
                <div className="font-display text-lg font-semibold">{p.name}</div>
                <p className="text-xs text-muted-foreground mt-1 h-8">{p.tagline}</p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="metric text-4xl">{price}</span>
                  {p.id !== "enterprise" && <span className="text-sm text-muted-foreground">/mo{p.id === "enterprise" ? "" : ""}</span>}
                </div>
                {p.id === "enterprise" && <span className="text-xs text-muted-foreground mt-1">from $2,000/mo</span>}
                {annual && p.id !== "enterprise" && <span className="text-xs text-emerald-500 mt-1">billed ${p.yearly}/yr</span>}
                <Button onClick={() => onSelectPlan(p)} data-testid={`select-${p.id}-btn`}
                  className={`mt-5 rounded-md w-full ${featured ? "bg-amber-500 text-black hover:bg-amber-400" : ""}`}
                  variant={featured ? "default" : "outline"}>
                  {p.id === "enterprise" ? "Contact sales" : "Choose plan"}
                </Button>
                <ul className="mt-6 space-y-2.5 text-sm">
                  {p.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2.5">
                      <Check className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                      <span className="text-muted-foreground">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-center text-xs text-muted-foreground max-w-2xl mx-auto">
          Add-ons: Apollo credit packs ($99 / $249 / $499), extra send volume ($79 / $149). Licensed Sales Navigator seat pass-through only where legally available (+$99/user/mo). Bring your own Navigator license - DealsOps never scrapes LinkedIn.
        </p>
      </section>

      {/* Comparison */}
      <section id="compare" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 border-t border-border/50">
        <div className="eyebrow mb-4 text-center">How we compare</div>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-center max-w-2xl mx-auto">Priced and built like Amplemarket - not a cheap email sender.</h2>
        <div className="mt-12 overflow-x-auto rounded-xl border border-border/70">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-border/70 bg-card/50">
                <th className="text-left font-medium p-4 w-[44%]">Capability</th>
                <th className="p-4 font-display font-semibold text-amber-500">DealsOps</th>
                <th className="p-4 font-medium text-muted-foreground">Basic senders</th>
                <th className="p-4 font-medium text-muted-foreground">Data-only tools</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row) => (
                <tr key={row[0]} className="border-b border-border/50 last:border-0">
                  <td className="p-4 text-muted-foreground">{row[0]}</td>
                  {row.slice(1).map((cell, j) => (
                    <td key={`${row[0]}-${j}`} className="p-4 text-center">
                      {cell === true ? <Check className="h-4 w-4 text-emerald-500 mx-auto" /> :
                       cell === "partial" ? <Minus className="h-4 w-4 text-amber-500 mx-auto" /> :
                       <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="rounded-2xl border border-amber-500/30 bg-card/60 p-10 sm:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-50" />
          <Sparkles className="h-8 w-8 text-amber-500 mx-auto mb-6 relative" />
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight relative">Signup to first approved campaign in under 30 minutes.</h2>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto relative">Describe your offer, approve the AI's first-touch emails, and launch compliant outbound today.</p>
          <Button onClick={() => navigate("/register")} data-testid="cta-bottom-btn" className="mt-8 rounded-md h-11 px-8 bg-amber-500 text-black hover:bg-amber-400 relative">Get started</Button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
